/**
 * Tender Workflow Service
 * Handles persistent workflow state management for tender creation process
 * Enables users to resume tender creation from where they left off
 */

import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';

// Workflow step definitions
// Workflow step definitions
export const WORKFLOW_STEPS = {
  CLIENT_SITES: 1,
  RETAILER_COMM: 2,
  EMAIL_PROCESSING: 3,
  OFFERS: 4,
  COMPARE: 5,
  RECOMMENDED: 6,
  TEMPLATE: 7,
  DETAILS: 8,
  OVERVIEW: 9
} as const;

export type WorkflowStep = typeof WORKFLOW_STEPS[keyof typeof WORKFLOW_STEPS];

// Workflow data structure
export interface TenderWorkflowData {
  step1?: {
    clientData?: any;
    sites?: any[];
    tenderName?: string;
    selectedClientId?: string;
    completed?: boolean;
  };
  step2?: {
    // Retailer Communication
    completed?: boolean;
    offers?: any[]; // Legacy support
  };
  step3?: {
    // Email Processing
    completed?: boolean;
    stagedData?: any;
  };
  step4?: {
    offers?: any[];
    completed?: boolean;
    recommendedOffers?: any[]; // Legacy support
  };
  step5?: {
    // Compare & Select
    completed?: boolean;
    selectedTemplate?: any; // Legacy support
    referenceDocument?: any; // Legacy support
  };
  step6?: {
    recommendedOffers?: any[];
    completed?: boolean;
    offerDetailsData?: any; // Legacy support
  };
  step7?: {
    selectedTemplate?: any;
    referenceDocument?: any;
    completed?: boolean;
    documentData?: any; // Legacy support
  };
  step8?: {
    offerDetailsData?: any;
    usageOverride?: {
      peak_kwh: number;
      offpeak_kwh: number;
      shoulder_kwh: number;
      total_kwh: number;
    };
    completed?: boolean;
  };
  step9?: {
    documentData?: any;
    completed?: boolean;
  };
}

// Tender summary for dashboard
export interface TenderSummary {
  id: string;
  opportunity_name: string;
  client_name?: string;
  current_step: number;
  completed_steps: number[];
  progress_percentage: number;
  last_accessed_at: string;
  created_at: string;
  status: string;
  site_count?: number;
  retailers_sent_count?: number;
  retailers_replied_count?: number;
  retailers_total_count?: number;
  last_email_sent_at?: string | null;
  first_reply_received_at?: string | null;
}

/**
 * Save tender workflow state to database
 */
export const saveTenderWorkflowState = async (
  tenderId: string,
  currentStep: WorkflowStep,
  stepData: any,
  isStepCompleted: boolean = false
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Fetch current state and prepare update in one roundtrip sequence
    // (we need current workflow_data to merge, so fetch is unavoidable — but we minimise fields)
    const { data: currentTender, error: fetchError } = await supabase
      .from('tender')
      .select('workflow_data, completed_steps')
      .eq('id', tenderId)
      .single();

    if (fetchError) throw new Error(`Failed to fetch tender: ${fetchError.message}`);

    const currentWorkflowData = (currentTender?.workflow_data as TenderWorkflowData) || {};
    const stepKey = `step${currentStep}` as keyof TenderWorkflowData;

    currentWorkflowData[stepKey] = {
      ...currentWorkflowData[stepKey],
      ...stepData,
      completed: isStepCompleted,
    };

    let completedSteps = currentTender?.completed_steps || [];
    if (isStepCompleted && !completedSteps.includes(currentStep)) {
      completedSteps = [...completedSteps, currentStep].sort();
    }

    const now = new Date().toISOString();
    const { error: updateError } = await supabase
      .from('tender')
      .update({
        current_step: currentStep,
        completed_steps: completedSteps,
        workflow_data: currentWorkflowData,
        last_accessed_at: now,
        updated_at: now,
      })
      .eq('id', tenderId);

    if (updateError) throw new Error(`Failed to update tender: ${updateError.message}`);

    console.log(`✅ Saved workflow state for tender ${tenderId}, step ${currentStep}`);
    return { success: true };

  } catch (error: any) {
    console.error('❌ Error saving tender workflow state:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Load tender workflow state from database
 */
export const loadTenderWorkflowState = async (
  tenderId: string
): Promise<{
  success: boolean;
  data?: {
    currentStep: WorkflowStep;
    completedSteps: number[];
    workflowData: TenderWorkflowData;
    tenderInfo: any;
    sites?: any[];
  };
  error?: string;
}> => {
  try {
    const { data: tender, error } = await supabase
      .from('tender')
      .select(`
        *,
        client:client_id (
          id,
          name,
          abn
        ),
        tender_site (*, site:site_id (document_file_paths, retailer_notes))
      `)
      .eq('id', tenderId)
      .single();

    if (error) {
      throw new Error(`Failed to load tender: ${error.message}`);
    }

    return {
      success: true,
      data: {
        currentStep: tender.current_step,
        completedSteps: tender.completed_steps || [],
        workflowData: tender.workflow_data || {},
        tenderInfo: tender,
        sites: tender.tender_site || []
      }
    };

  } catch (error: any) {
    console.error('❌ Error loading tender workflow state:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Persist the tender's contract-term series — which month count each Year N is.
 *
 * Lives on `tender.extn.term_series` rather than a column of its own so this needs no
 * migration. Merged into whatever else extn holds, never replacing it.
 *
 * ⚠️ The series is what stops a term being labelled by snapping to the ORG-WIDE
 * sys_config.requested_contract_terms list. A tender that asked for 4/16/28/40 had nowhere to
 * record that, so 4 and 16 both snapped to the 12-month bucket and collided on Year 1. See
 * lib/tenderTermSeries.ts for the full account.
 */
export const saveTenderTermSeries = async (
  tenderId: string,
  series: Record<string, number | string>,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Read-modify-write on the jsonb: extn carries unrelated keys and a blind overwrite
    // would drop them.
    const { data: current, error: fetchError } = await supabase
      .from('tender')
      .select('extn')
      .eq('id', tenderId)
      .single();
    if (fetchError) throw new Error(`Failed to read tender extn: ${fetchError.message}`);

    const extn = (current?.extn && typeof current.extn === 'object') ? current.extn : {};

    const { error: updateError } = await supabase
      .from('tender')
      .update({ extn: { ...extn, term_series: series }, updated_at: new Date().toISOString() })
      .eq('id', tenderId);
    if (updateError) throw new Error(`Failed to save term series: ${updateError.message}`);

    console.log(`✅ Saved term series for tender ${tenderId}:`, series);
    return { success: true };
  } catch (error: any) {
    console.error('❌ Error saving tender term series:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get list of user's tenders for dashboard
 */
export const getUserTenders = async (): Promise<{
  success: boolean;
  data?: TenderSummary[];
  error?: string;
}> => {
  try {
    // Get auth user and org in parallel
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: orgMember } = await supabase
      .from('base_organisation_members')
      .select('organisation_id')
      .eq('user_id', user.id)
      .single();

    if (!orgMember) throw new Error('User organization not found');

    const { data: tenders, error } = await supabase
      .from('tender')
      .select(`
        id,
        opportunity_name,
        current_step,
        completed_steps,
        last_accessed_at,
        created_at,
        status,
        retailers_sent_count,
        retailers_replied_count,
        retailers_total_count,
        last_email_sent_at,
        first_reply_received_at,
        client:client_id (
          name
        ),
        tender_site (count)
      `)
      .eq('org_id', orgMember.organisation_id)
      .order('last_accessed_at', { ascending: false })
      .limit(50);

    if (error) {
      throw new Error(`Failed to fetch tenders: ${error.message}`);
    }

    // Calculate progress percentage for each tender
    const tendersWithProgress: TenderSummary[] = (tenders || []).map(tender => {
      const completedSteps = tender.completed_steps || [];
      const progressPercentage = Math.round((completedSteps.length / 6) * 100);

      return {
        id: tender.id,
        opportunity_name: tender.opportunity_name || 'Untitled Tender',
        client_name: (tender.client as any)?.name,
        current_step: tender.current_step,
        completed_steps: completedSteps,
        progress_percentage: progressPercentage,
        last_accessed_at: tender.last_accessed_at,
        created_at: tender.created_at,
        status: tender.status,
        site_count: tender.tender_site?.[0]?.count || 0,
        retailers_sent_count: tender.retailers_sent_count || 0,
        retailers_replied_count: tender.retailers_replied_count || 0,
        retailers_total_count: tender.retailers_total_count || 0,
        last_email_sent_at: tender.last_email_sent_at,
        first_reply_received_at: tender.first_reply_received_at
      };
    });

    return { success: true, data: tendersWithProgress };

  } catch (error: any) {
    console.error('❌ Error fetching user tenders:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create new tender with initial workflow state
 */
export const createNewTender = async (
  clientId: string,
  tenderName: string,
  initialData?: any
): Promise<{ success: boolean; tenderId?: string; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get user's organization
    const { data: orgMember } = await supabase
      .from('base_organisation_members')
      .select('organisation_id')
      .eq('user_id', user.id)
      .single();

    if (!orgMember) {
      throw new Error('User organization not found');
    }

    // Create tender with initial workflow state
    const { data: tender, error } = await supabase
      .from('tender')
      .insert({
        org_id: orgMember.organisation_id,
        client_id: clientId,
        owner_user_id: user.id,
        opportunity_name: tenderName,
        status: 'draft',
        current_step: 1,
        completed_steps: [],
        workflow_data: initialData || {},
        last_accessed_at: new Date().toISOString()
      })
      .select('id')
      .single();

    if (error) {
      throw new Error(`Failed to create tender: ${error.message}`);
    }

    console.log(`✅ Created new tender: ${tender.id}`);
    return { success: true, tenderId: tender.id };

  } catch (error: any) {
    console.error('❌ Error creating new tender:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete tender
 */
export const deleteTender = async (
  tenderId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('tender')
      .delete()
      .eq('id', tenderId);

    if (error) {
      throw new Error(`Failed to delete tender: ${error.message}`);
    }

    console.log(`✅ Deleted tender: ${tenderId}`);
    return { success: true };

  } catch (error: any) {
    console.error('❌ Error deleting tender:', error);
    return { success: false, error: error.message };
  }
};

/**
 * The consultant whose name and contact details print on the proposal's last page.
 *
 * ⚠️ WHY THIS IS STORED ON THE TENDER AND NOT DERIVED AT GENERATION TIME.
 *
 * Generation resolved it in this order: the Salesforce tender owner at
 * `tender.extn.consultant`, and failing that the `base_profiles` row of WHOEVER CLICKED
 * GENERATE. That second branch is wrong twice over.
 *
 * It is wrong for the client: an admin regenerating someone else's tender silently replaces the
 * consultant's contact details with their own, and the client is told to ring the wrong person.
 *
 * It is wrong because it cannot work. Measured on prod: 272 of 899 tenders carry no Salesforce
 * consultant at all, and every one of the 15 rows in base_profiles has an email but NO phone —
 * so for those tenders the phone could never be filled from any source. A further 89 tenders
 * have a Salesforce consultant with an email and no phone. That is 361 tenders, 40%, where
 * {consultant_mobile} and {consultant_phone} had nothing to resolve to.
 *
 * Captured on Step 1 instead, pre-filled from the same two sources, so the common case still
 * costs no typing and the value is fixed to the TENDER rather than to the person exporting it.
 *
 * Lives on `tender.extn.consultant` — the key generation already reads first, so no new
 * plumbing and no migration. Merged into extn, never replacing it.
 */
export type TenderConsultant = {
  name: string;
  email: string;
  phone: string;
};

/**
 * F9 — the name the cover is addressed to, when the team have overridden it.
 *
 * Their words: *"If multi-site has multiple entity names, we want to make the 'Prepared for:'
 * customizable."* A tender covering several legal entities has no single Client record that is
 * the truthful answer, so the broker states it.
 *
 * Stored on `tender.extn.cover.preparedFor` — the same jsonb column the consultant and the term
 * series already live in, so this needs no schema change. Absent means "use the Client record",
 * which is every tender that has not been overridden.
 */
export const getTenderPreparedFor = async (tenderId: string): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('tender').select('extn').eq('id', tenderId).single();
    if (error) throw new Error(error.message);
    const cover = (data?.extn as Record<string, unknown> | null)?.cover as
      | Record<string, unknown> | null | undefined;
    const value = cover && typeof cover === 'object' ? cover.preparedFor : null;
    return typeof value === 'string' ? value.trim() : '';
  } catch (error: unknown) {
    console.error('❌ Error reading tender "Prepared for":', error);
    return '';
  }
};

/**
 * Persist the cover's "Prepared for" name. An empty string CLEARS the override, so the cover
 * goes back to the Client record rather than being stuck on a name the user has just deleted.
 */
export const saveTenderPreparedFor = async (
  tenderId: string,
  preparedFor: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Read-modify-write: extn carries the consultant and the term series too.
    const { data: current, error: fetchError } = await supabase
      .from('tender').select('extn').eq('id', tenderId).single();
    if (fetchError) throw new Error(`Failed to read tender extn: ${fetchError.message}`);

    const extn = (current?.extn && typeof current.extn === 'object') ? current.extn as Record<string, unknown> : {};
    const cover = (extn.cover && typeof extn.cover === 'object') ? extn.cover as Record<string, unknown> : {};
    const trimmed = preparedFor.trim();

    const { error: updateError } = await supabase
      .from('tender')
      .update({
        extn: { ...extn, cover: { ...cover, preparedFor: trimmed || null } },
        updated_at: new Date().toISOString(),
      })
      .eq('id', tenderId);
    if (updateError) throw new Error(`Failed to save "Prepared for": ${updateError.message}`);

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Error saving tender "Prepared for":', message);
    return { success: false, error: message };
  }
};

/** Read the consultant stored on a tender. Absent keys come back as empty strings. */
export const getTenderConsultant = async (
  tenderId: string,
): Promise<TenderConsultant | null> => {
  try {
    const { data, error } = await supabase
      .from('tender')
      .select('extn')
      .eq('id', tenderId)
      .single();
    if (error) throw new Error(error.message);

    const stored = (data?.extn as Record<string, unknown> | null)?.consultant as
      | Record<string, unknown> | null | undefined;
    if (!stored || typeof stored !== 'object') return null;

    const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
    return {
      name: str(stored.name),
      email: str(stored.email),
      // Salesforce carries these as separate fields and either may be the one populated.
      phone: str(stored.mobile) || str(stored.phone),
    };
  } catch (error: unknown) {
    console.error('❌ Error reading tender consultant:', error);
    return null;
  }
};

/**
 * Persist the consultant onto the tender.
 *
 * `phone` is written to BOTH `phone` and `mobile` because generation reads
 * `(mobile ?? phone)` and the proposal template has separate {consultant_mobile} and
 * {consultant_phone} tokens; one number answers both rather than asking for the same digits
 * twice.
 */
export const saveTenderConsultant = async (
  tenderId: string,
  consultant: TenderConsultant,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Read-modify-write: extn carries unrelated keys and a blind overwrite would drop them.
    const { data: current, error: fetchError } = await supabase
      .from('tender')
      .select('extn')
      .eq('id', tenderId)
      .single();
    if (fetchError) throw new Error(`Failed to read tender extn: ${fetchError.message}`);

    const extn = (current?.extn && typeof current.extn === 'object') ? current.extn : {};
    const existing = (extn as Record<string, unknown>).consultant;
    const merged = {
      ...(existing && typeof existing === 'object' ? existing : {}),
      name: consultant.name.trim(),
      email: consultant.email.trim(),
      phone: consultant.phone.trim(),
      mobile: consultant.phone.trim(),
    };

    const { error: updateError } = await supabase
      .from('tender')
      .update({ extn: { ...extn, consultant: merged }, updated_at: new Date().toISOString() })
      .eq('id', tenderId);
    if (updateError) throw new Error(`Failed to save consultant: ${updateError.message}`);

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Error saving tender consultant:', message);
    return { success: false, error: message };
  }
};
